'use client';

import React, {
  type KeyboardEvent,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { styledScrollbarMixin } from 'components/ScrollContainer';
import type { Descendant } from 'hooks/useDescendants';
import { DescendantsProvider } from 'hooks/useDescendants';
import styled from 'styled-components';
import { getAdaptivePopoverWidthCSS } from 'theme-utils/utils';
import { getInteractionModality } from '@react-aria/interactions';
import { mergeRefs } from '@react-aria/utils';
import type { AriaLabelingProps } from '@react-types/shared';
import {
  findItemFromTypeahead,
  getChildIndexById,
  getFirstEnabledChildIndex,
  getLastEnabledChildIndex,
  getNextEnabledChild,
  getPrevEnabledChild,
  hasDefaultSelected,
} from '../../hooks/useDescendants/traversal-helpers';
import { PopoverStatusContext } from '../Popover/PopoverStatusContext';
import { MenuListContext } from './MenuListContext';
import { MovingTowardSubmenuProvider } from './useMovingTowardSubmenu';

const defaultMenuListState: State & { shouldFocusChild: boolean } = {
  selectedId: undefined,
  expandedId: undefined,
  shouldFocusChild: false,
  typeahead: '',
};

type Action =
  | { type: 'SELECT'; payload: { id: string } }
  | { type: 'DESELECT' }
  | { type: 'COLLAPSE' }
  | { type: 'COLLAPSE_AND_DESELECT' }
  | { type: 'SELECT_AND_EXPAND'; payload: { id: string } }
  | { type: 'SELECT_AND_COLLAPSE'; payload: { id: string } }
  | { type: 'EXPAND_AND_FOCUS_CHILD'; payload: { id: string } }
  | { type: 'SEARCH'; payload: string };

type State = {
  selectedId?: string;
  expandedId?: string;
  typeahead: string;
};

function menuListReducer(state: State, action: Action) {
  switch (action.type) {
    case 'SELECT': {
      return {
        ...state,
        selectedId: action.payload.id,
      };
    }
    case 'DESELECT': {
      return {
        ...state,
        selectedId: null,
      };
    }
    case 'COLLAPSE': {
      return {
        ...state,
        expandedId: null,
      };
    }
    case 'COLLAPSE_AND_DESELECT': {
      return {
        ...state,
        expandedId: null,
        selectedId: null,
      };
    }
    case 'SELECT_AND_EXPAND': {
      const { id } = action.payload;
      return {
        ...state,
        selectedId: id,
        expandedId: id,
      };
    }
    case 'SELECT_AND_COLLAPSE': {
      return {
        ...state,
        selectedId: action.payload.id,
        expandedId: null,
      };
    }
    case 'EXPAND_AND_FOCUS_CHILD': {
      return {
        ...state,
        expandedId: action.payload.id,
        selectedId: null,
      };
    }
    case 'SEARCH': {
      if (typeof action.payload !== 'undefined') {
        return {
          ...state,
          typeahead: action.payload,
        };
      }
      return state;
    }
    default:
      return state;
  }
}

type StyledMenuListProps = {
  $minWidth: string;
  $maxWidth: string;
  $width?: string;
};

export const StyledMenuList = styled.div<StyledMenuListProps>`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: ${(p) => p.$maxWidth};
  min-width: ${(p) => p.$minWidth};
  outline: none;
  overflow-y: auto;
  padding: 0;
  ${styledScrollbarMixin};
  width: ${(p) => p.$width};
  @supports (scrollbar-width: auto) {
    scrollbar-gutter: auto;
    scrollbar-width: thin;
  }
`;

export type MenuListProps =
  /**
   * Used to label the menu for screen readers
   * from https://react-spectrum.adobe.com/react-aria/useMenu.html#api
   */
  AriaLabelingProps &
    PropsWithChildren<{
      className?: string;
      minWidth?: string;
      maxWidth?: string;
      width?: string;
      /** an event callback for when the Menu does not handle ArrowLeft */
      onArrowKeyLeft?: (e: React.KeyboardEvent) => void;
      /** an event callback for when the Menu does not handle ArrowRight */
      onArrowKeyRight?: (e: React.KeyboardEvent) => void;
    }>;

/**
 * MenuList
 *
 * Controls keyboard navigation and selected / expanded
 * states for a collection of menu items.
 */

export const MenuList = React.forwardRef<HTMLDivElement, MenuListProps>(
  (
    {
      minWidth = getAdaptivePopoverWidthCSS({ multiplier: 3 }),
      maxWidth = getAdaptivePopoverWidthCSS({ multiplier: 6 }),
      width,
      children,
      onArrowKeyLeft,
      onArrowKeyRight,
      ...other
    },
    forwardedRef
  ) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const { isShowing } = useContext(PopoverStatusContext);
    const [descendants, setDescendants] = useState<Array<Descendant>>([]);
    const [{ expandedId, selectedId, typeahead }, dispatch] = useReducer(
      // @ts-expect-error FIXME(strictNullChecks)
      menuListReducer,
      defaultMenuListState
    );

    /**
     * Select the first enabled MenuItem child of a MenuList when our parent
     * Menu (or MenuList) wants us to focus that child. This is used
     * when a MenuList is opened via keyboard.
     */

    useEffect(() => {
      const modality = getInteractionModality();
      const alreadyHasFocus = menuRef.current?.contains(document.activeElement);
      const listHasDefaultSelected = hasDefaultSelected(descendants);
      if (
        isShowing &&
        modality === 'keyboard' &&
        descendants.length &&
        !alreadyHasFocus &&
        !listHasDefaultSelected
      ) {
        requestAnimationFrame(() => {
          const firstEnabledChild =
            descendants[getFirstEnabledChildIndex(descendants)];

          if (firstEnabledChild) {
            firstEnabledChild.element.current.focus();
            dispatch({
              type: 'SELECT',
              payload: { id: firstEnabledChild.id },
            });
          }
        });
      }
    }, [descendants, isShowing]);

    /**
     * Handle "Typeahead" functionality.
     */

    useEffect(() => {
      const matchingChild = findItemFromTypeahead(descendants, typeahead);

      if (typeahead && matchingChild) {
        dispatch({
          type: 'SELECT_AND_COLLAPSE',
          payload: { id: matchingChild.id },
        });
      }

      // Reset our search query after 750ms.
      const timeout = window.setTimeout(
        () => typeahead && dispatch({ type: 'SEARCH', payload: '' }),
        750
      );

      return () => window.clearTimeout(timeout);
    }, [descendants, typeahead]);

    const focusNextEnabledChild = useCallback(() => {
      // @ts-expect-error FIXME(strictNullChecks)
      const nextEnabledChild = getNextEnabledChild(selectedId, descendants);
      if (nextEnabledChild) {
        dispatch({
          type: 'SELECT_AND_COLLAPSE',
          payload: { id: nextEnabledChild.id },
        });
      }
    }, [descendants, selectedId]);

    const focusPrevEnabledChild = useCallback(() => {
      // @ts-expect-error FIXME(strictNullChecks)
      const prevEnabledChild = getPrevEnabledChild(selectedId, descendants);
      dispatch({
        type: 'SELECT_AND_COLLAPSE',
        payload: { id: prevEnabledChild.id },
      });
    }, [descendants, selectedId]);

    /**
     * Handle keyboard accessibility.
     *
     * We handle most of these events at the MenuList
     * level because we can't assume that a MenuItem
     * will always have a focus state.
     */

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        function handleEvent() {
          e.stopPropagation();
          e.preventDefault();
        }

        switch (e.key) {
          // Select the first enabled child.
          case 'Home': {
            handleEvent();
            const firstEnabledChild =
              descendants[getFirstEnabledChildIndex(descendants)];

            if (firstEnabledChild) {
              dispatch({
                type: 'SELECT_AND_COLLAPSE',
                payload: { id: firstEnabledChild.id },
              });
            }
            break;
          }

          // Collapse our MenuList.
          case 'ArrowLeft': {
            if (expandedId) {
              handleEvent();
              dispatch({
                type: 'SELECT_AND_COLLAPSE',
                payload: { id: expandedId },
              });
            } else {
              onArrowKeyLeft?.(e);
            }
            break;
          }

          case 'ArrowRight': {
            onArrowKeyRight?.(e);
            break;
          }

          // Select the last enabled child.
          case 'End': {
            handleEvent();
            const lastEnabledChild =
              descendants[getLastEnabledChildIndex(descendants)];
            if (lastEnabledChild) {
              dispatch({
                type: 'SELECT_AND_COLLAPSE',
                payload: { id: lastEnabledChild.id },
              });
            }
            break;
          }

          // Select the next enabled child.
          case 'ArrowDown': {
            handleEvent();
            focusNextEnabledChild();
            break;
          }

          // Select the previous enabled child.
          case 'ArrowUp': {
            handleEvent();
            focusPrevEnabledChild();
            break;
          }

          // Prevent default tab behavior.
          case 'Tab':
            handleEvent();
            break;

          default:
            // Trigger typeahead functionality
            if (typeof e.key === 'string' && e.key.length === 1) {
              handleEvent();
              dispatch({
                type: 'SEARCH',
                payload: typeahead + e.key.toLowerCase(),
              });
              break;
            }
        }
      },
      [
        descendants,
        expandedId,
        focusNextEnabledChild,
        focusPrevEnabledChild,
        onArrowKeyLeft,
        onArrowKeyRight,
        typeahead,
      ]
    );

    /**
     * On mouse-enter, collapse any expanded submenus.
     */

    const handleMouseEnter = useCallback(
      (e: MouseEvent) => {
        if (expandedId) {
          const expandedIndex = getChildIndexById(expandedId, descendants);
          const expandedElement =
            descendants[expandedIndex].containerRef.current;
          if (expandedElement && !expandedElement.contains(e.target)) {
            dispatch({ type: 'COLLAPSE' });
            // @ts-expect-error FIXME(strictNullChecks)
            menuRef.current.focus();
          }
        }
      },
      [descendants, expandedId, menuRef]
    );

    const menuContext = useMemo(
      () => ({
        expandedId,
        selectedId,
        dispatch,
        menuRef,
        focusNextEnabledChild,
        focusPrevEnabledChild,
      }),
      [expandedId, focusNextEnabledChild, focusPrevEnabledChild, selectedId]
    );

    return (
      <StyledMenuList
        ref={mergeRefs(menuRef, forwardedRef)}
        $maxWidth={maxWidth}
        $minWidth={minWidth}
        $width={width}
        aria-orientation="vertical"
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        role="menu"
        tabIndex={-1}
        {...other}
      >
        <DescendantsProvider
          descendants={descendants}
          setDescendants={setDescendants}
        >
          <MovingTowardSubmenuProvider>
            {/* @ts-expect-error FIXME(strictNullChecks) */}
            <MenuListContext.Provider value={menuContext}>
              {children}
            </MenuListContext.Provider>
          </MovingTowardSubmenuProvider>
        </DescendantsProvider>
      </StyledMenuList>
    );
  }
);

export const testExports = {
  menuListReducer,
  defaultMenuListState,
};
